import { SelectMenu } from '@scale/scaleui';
import { twMerge } from 'tailwind-merge';
import { ICustomCodeEnvironment } from '@scale/shared/types/config';
import {
  LANGUAGE_OPTIONS as SUPPORTED_EXECUTION_LANGUAGES,
  SUPPORTED_PRETTY_PRINT_FORMATS,
  fullLanguageMap,
} from '@scale/shared/sphereEngine/types';
import { useMemo } from 'react';

interface ILanguageDropdownProps {
  className?: string;
  selectMenuClassName?: string;
  selectMenuButtonClassName?: string;
  selectMenuOptionClassName?: string;
  customCodeEnvironments?: ICustomCodeEnvironment[];
  selectedLanguage: string;
  selectedCustomCodeEnvironment?: ICustomCodeEnvironment | null;
  useFullLanguageOptions?: boolean;
  disabled?: boolean;
  handleLanguageChange: (language: string) => void;
  setSelectedCustomCodeEnvironment?: (
    customCodeEnvironment?: ICustomCodeEnvironment | null,
  ) => void;
  onBlur?: () => void;
}

/**
 * TODO: 04/15/2024 - will likely revamp this component to move off of the SelectMenu ScaleUI component
 * and implement a custom dropdown component instead.
 */
export const LanguageDropdown = ({
  className,
  selectMenuClassName,
  selectMenuButtonClassName,
  selectMenuOptionClassName,
  customCodeEnvironments,
  selectedLanguage,
  selectedCustomCodeEnvironment,
  useFullLanguageOptions,
  disabled,
  handleLanguageChange,
  setSelectedCustomCodeEnvironment,
  onBlur,
}: ILanguageDropdownProps) => {
  const fullLanguageOptions = useMemo(
    () =>
      Object.keys(fullLanguageMap)
        .sort()
        .map(option => ({
          id: option,
          name: fullLanguageMap[option],
        })),
    [],
  );

  const environmentOptions = useMemo(() => {
    // If custom code environments are provided, use them as the options, otherwise use the default supported languages
    if (customCodeEnvironments && customCodeEnvironments.length > 0) {
      const options = customCodeEnvironments.map(env => ({
        id: env.name,
        name: '*' + env.name,
      }));

      const additionalLanguageOptions = useFullLanguageOptions
        ? fullLanguageOptions
        : [...SUPPORTED_EXECUTION_LANGUAGES, ...SUPPORTED_PRETTY_PRINT_FORMATS];

      const optionIds = new Set<string>(options.map(option => option.id));
      return [...options, ...additionalLanguageOptions.filter(option => !optionIds.has(option.id))];
    }
    return useFullLanguageOptions
      ? fullLanguageOptions
      : [...SUPPORTED_EXECUTION_LANGUAGES, ...SUPPORTED_PRETTY_PRINT_FORMATS];
  }, [customCodeEnvironments, useFullLanguageOptions, fullLanguageOptions]);

  return (
    <div className={twMerge('flex flex-row z-[3] transform scale-[0.85]', className)}>
      <SelectMenu<string>
        placeholder="Select Environment"
        className={selectMenuClassName}
        menuDirection="bottom"
        selectedOptionId={selectedCustomCodeEnvironment?.name ?? selectedLanguage}
        setSelected={(option: string) => {
          const customCodeEnv = customCodeEnvironments?.find(env => env.name === option);
          if (customCodeEnv) {
            handleLanguageChange(customCodeEnv.language);
            setSelectedCustomCodeEnvironment && setSelectedCustomCodeEnvironment(customCodeEnv);
          } else {
            handleLanguageChange(option);
            setSelectedCustomCodeEnvironment && setSelectedCustomCodeEnvironment(null);
          }
        }}
        options={environmentOptions}
        floatStrategy="fixed"
        buttonClassName={selectMenuButtonClassName}
        optionClassName={selectMenuOptionClassName}
        onBlur={onBlur}
        disabled={disabled}
      />
    </div>
  );
};
